// The driver's own schedule in the staff app (PMU-254): the next two weeks of runs (with the
// calendar and the dispatch board) and time off, asked from the phone.
import { useEffect, useState } from "react";
import { Badge, Banner, Button, Dialog, DialogClose, EmptyState, Select, Spinner, TextField } from "@vavr/ui";
import { api } from "@vavr/screens/shared";

interface Day { date: string; runs: { route_id: string; name: string; kind: string; start: string | null; end: string | null; bus: string | null; note: string | null }[] }
interface Off { id: string; from_date: string; to_date: string; part: "day" | "am" | "pm"; reason: string | null; status: "requested" | "approved" | "declined" }
const day = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" });
const PART = { day: "All day", am: "Morning", pm: "Afternoon" };
const STATUS = { requested: { label: "Waiting", tone: "info" }, approved: { label: "Approved", tone: "success" }, declined: { label: "Declined", tone: "danger" } } as const;

export function MySchedule() {
  const [data, setData] = useState<{ today: string; days: Day[]; time_off: Off[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => api<{ today: string; days: Day[]; time_off: Off[] }>("GET", "/api/v1/transport/me/schedule").then(setData, (e: Error) => setError(e.message));
  useEffect(() => { void load(); }, []);
  if (!data) return error ? <Banner tone="danger" title={error} /> : <div className="pmu-center"><Spinner label="Loading your schedule" /></div>;
  const withRuns = data.days.filter((d) => d.runs.length);
  return (
    <div className="pmt-wide pmu-stack">
      <AskTimeOff today={data.today} onDone={() => void load()} />
      {data.time_off.length > 0 && (
        <section className="pmu-stack">
          <h2 className="pmt-card__title">Time off</h2>
          {data.time_off.map((t) => (
            <div key={t.id} className="pmt-sched__off">
              <span><b>{t.from_date === t.to_date ? day(t.from_date) : `${day(t.from_date)} – ${day(t.to_date)}`}</b>{t.part !== "day" ? ` · ${PART[t.part]}` : ""}{t.reason ? <span className="pmu-muted"> · {t.reason}</span> : null}</span>
              <span className="pmu-row"><Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge>{t.status !== "declined" && <Button size="sm" variant="ghost" onClick={async () => { await api("DELETE", `/api/v1/transport/me/time-off/${t.id}`); void load(); }}>Take back</Button>}</span>
            </div>
          ))}
        </section>
      )}
      <section className="pmu-stack">
        <h2 className="pmt-card__title">My runs</h2>
        {!withRuns.length ? <EmptyState title="No runs in the next two weeks" description="Your dispatcher puts you on runs; they show here." /> : withRuns.map((d) => (
          <div key={d.date} className="pmt-sched__day">
            <h3>{d.date === data.today ? "Today" : day(d.date)}</h3>
            {d.runs.map((r) => (
              <div key={r.route_id} className="pmt-sched__run">
                <b>{r.start ?? "--:--"}{r.end ? `–${r.end}` : ""}</b>
                <span>{r.name}{r.bus ? <span className="pmu-muted"> · {r.bus}</span> : null}{r.note ? <span className="pmu-small pmu-muted"><br />{r.note}</span> : null}</span>
              </div>
            ))}
          </div>
        ))}
      </section>
    </div>
  );
}

function AskTimeOff({ today, onDone }: { today: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ from_date: today, to_date: today, part: "day", reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true); setError(null);
    try { await api("POST", "/api/v1/transport/me/time-off", { ...f, reason: f.reason || null }); setOpen(false); onDone(); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <Dialog title="Ask for time off" description="Your dispatcher approves it and finds someone for your runs." open={open} onOpenChange={(v) => { setOpen(v); if (v) { setF({ from_date: today, to_date: today, part: "day", reason: "" }); setError(null); } }}
      trigger={<Button icon="plus" fullWidth variant="secondary">Ask for time off</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button disabled={f.to_date < f.from_date} loading={busy} onClick={() => void send()}>Send</Button></>}>
      <div className="pmu-stack">
        {error && <Banner tone="danger" title={error} />}
        <TextField label="First day" type="date" min={today} value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value, to_date: f.to_date < e.target.value ? e.target.value : f.to_date })} />
        <TextField label="Last day" type="date" min={f.from_date} value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} />
        <Select label="Part of the day" value={f.part} onValueChange={(v) => setF({ ...f, part: v ?? "day" })} options={[{ value: "day", label: "All day" }, { value: "am", label: "Mornings" }, { value: "pm", label: "Afternoons" }]} />
        <TextField label="Reason (optional)" maxLength={200} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
      </div>
    </Dialog>
  );
}
